import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ApiError, SERVER_KEY_SCOPES, type IssuedServerKey } from '../api/client.ts';
import { CopyId } from '@bb/ui';
import { Can } from '../features/access/Can.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const STATUS_LABEL = { active: 'Active', retiring: 'Retiring', retired: 'Retired', expired: 'Expired', revoked: 'Revoked' } as const;

/** Settings → APIs and Identifiers: ids to copy into SDK/backend config, and server API keys (BB-17). */
export function ApiPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/api' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/api' });
  const ids = useQuery({ queryKey: ['identifiers', environmentId], queryFn: () => api.identifiers(environmentId) });

  return (
    <section>
      <h1>APIs and Identifiers</h1>
      {ids.isPending ? (
        <p aria-busy="true">Loading identifiers…</p>
      ) : ids.isError ? (
        <p role="alert">Identifiers could not be loaded.</p>
      ) : (
        <>
          <dl className="ids">
            <dt>Environment ID</dt>
            <dd>
              <CopyId value={ids.data.environment.id} label="environment ID" />
            </dd>
            <dt>Environment key</dt>
            <dd>
              <CopyId value={ids.data.environment.key} label="environment key" />
            </dd>
            <dt>Server API</dt>
            <dd>
              <code>{ids.data.serverApi.baseUrl}</code> with <code>Authorization: Bearer {ids.data.serverApi.keyFormat}</code>
            </dd>
          </dl>
          <h2>App API IDs</h2>
          {ids.data.apps.length === 0 ? (
            <p className="empty-state">No apps registered in this environment.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">App</th>
                  <th scope="col">Identifier</th>
                  <th scope="col">API ID</th>
                </tr>
              </thead>
              <tbody>
                {ids.data.apps.map((app) => (
                  <tr key={app.id}>
                    <td>{app.name}</td>
                    <td>
                      <code>{app.identifier}</code>
                    </td>
                    <td>
                      <CopyId value={app.apiId} label={`API ID of ${app.name}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <Can permission="keys.manage">
        <ServerKeys environmentId={environmentId} />
      </Can>
    </section>
  );
}

function ServerKeys({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/api' });
  const queryClient = useQueryClient();
  const queryKey = ['server-keys', environmentId];
  const keys = useQuery({ queryKey, queryFn: () => api.listServerKeys(environmentId) });
  const [issued, setIssued] = useState<IssuedServerKey | null>(null);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [allowlist, setAllowlist] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const onIssued = (result: IssuedServerKey) => (setIssued(result), refresh());

  const create = useMutation({
    mutationFn: () =>
      api.createServerKey(environmentId, {
        name: name.trim(),
        scopes,
        ...(allowlist.trim() ? { ipAllowlist: allowlist.split(/[\s,]+/).filter(Boolean) } : {}),
      }),
    onSuccess: (result) => (onIssued(result), setName(''), setScopes([]), setAllowlist('')),
  });
  const rotate = useMutation({ mutationFn: (keyId: string) => api.rotateServerKey(environmentId, keyId, 24), onSuccess: onIssued });
  const revoke = useMutation({ mutationFn: (keyId: string) => api.revokeServerKey(environmentId, keyId), onSuccess: refresh });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() && scopes.length > 0) create.mutate();
  };
  const toggle = (scope: string) => setScopes((current) => (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]));

  return (
    <section aria-label="Server API keys" className="server-keys">
      <h2>Server API keys</h2>
      {issued && (
        <div role="alert" className="issued-key">
          <p>
            New key for <strong>{issued.serverKey.name}</strong>. Copy it now: it is shown only once.
          </p>
          <CopyId value={issued.key} label="new server key" />
          <button type="button" onClick={() => setIssued(null)}>
            I have stored the key
          </button>
        </div>
      )}
      {keys.isPending ? (
        <p aria-busy="true">Loading keys…</p>
      ) : keys.isError ? (
        <p role="alert">Keys could not be loaded.</p>
      ) : keys.data.length === 0 ? (
        <p className="empty-state">No server keys yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Key</th>
              <th scope="col">Scopes</th>
              <th scope="col">Status</th>
              <th scope="col">Last used</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.data.map((key) => (
              <tr key={key.id}>
                <td>{key.name}</td>
                <td>
                  <code>{key.keyPrefix}…</code>
                </td>
                <td>{key.scopes.join(', ')}</td>
                <td>
                  {STATUS_LABEL[key.status]}
                  {key.status === 'retiring' && key.retiringUntil ? ` until ${key.retiringUntil.slice(0, 16).replace('T', ' ')}` : ''}
                </td>
                <td>{key.lastUsedAt ? `${key.lastUsedAt.slice(0, 16).replace('T', ' ')} from ${key.lastUsedIp ?? '?'}` : 'Never'}</td>
                <td className="actions">
                  {key.status === 'active' && (
                    <button type="button" onClick={() => rotate.mutate(key.id)} disabled={rotate.isPending} aria-label={`Rotate ${key.name}`}>
                      Rotate
                    </button>
                  )}
                  {(key.status === 'active' || key.status === 'retiring') && (
                    <button type="button" onClick={() => revoke.mutate(key.id)} disabled={revoke.isPending} aria-label={`Revoke ${key.name}`}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(rotate.isError || revoke.isError) && <p role="alert">{errorText(rotate.error ?? revoke.error)}</p>}

      <form onSubmit={submit} aria-label="Create server key" className="stack-form">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VCG backend" />
        </label>
        <fieldset>
          <legend>Scopes</legend>
          {SERVER_KEY_SCOPES.map((scope) => (
            <label key={scope}>
              <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggle(scope)} /> {scope}
            </label>
          ))}
        </fieldset>
        <label>
          IP allowlist (optional, CIDR, comma separated)
          <input value={allowlist} onChange={(e) => setAllowlist(e.target.value)} placeholder="203.0.113.0/24" />
        </label>
        <button type="submit" disabled={create.isPending || !name.trim() || scopes.length === 0}>
          Create key
        </button>
        {create.isError && <span role="alert">{errorText(create.error)}</span>}
      </form>
    </section>
  );
}
